/** The chat's duo: the two who replied to each other most, flying in to meet in the middle. */

import { Avatar, Fit, Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import type { Chat } from "../../types";
import { count } from "../shared";

const BLOBS: BlobSpec[] = [
  { colour: "yellow", x: 1000, y: 1800, r: 270, seed: 36.1, wob: 0.2 },
  { colour: "blurple", x: 60, y: 300, r: 140, seed: 37.3, wob: 0.24 },
];

const BAR = 936;

export function Duo({ duo }: { duo: NonNullable<Chat["duo"]> }) {
  const total = duo.a_to_b + duo.b_to_a;
  const split = total ? Math.round((duo.a_to_b / total) * (BAR - 10)) : (BAR - 10) / 2;

  const root = useTimeline((s) => {
    s.rise(".kick", 0.35, { y: 0 });
    s.rise("[data-part=lead]", 0.45, { y: 30, sound: false });
    // They fly in from either side and bump in the middle.
    s.tl.fromTo("[data-part=a]", { x: -760 }, { x: 0, duration: 0.9, ease: "elastic.out(1, 0.6)" }, 0.6);
    s.tl.fromTo("[data-part=b]", { x: 760 }, { x: 0, duration: 0.9, ease: "elastic.out(1, 0.6)" }, 0.6);
    s.sound("whoosh", 0.6, 0.9, { pan: -0.7, length: 0.45 });
    s.sound("whoosh", 0.62, 1.1, { pan: 0.7, length: 0.45 });
    s.sound("knock", 0.95, 0.8);
    s.pop("[data-part=and]", 1.15, { from: 0.2 });
    s.rise("[data-part=names] > *", 1.4, { y: 30, stagger: 0.1 });
    s.rise("[data-part=total]", 1.8, { y: 40, sound: false });
    s.count(s.q("[data-part=total]")[0], total, 1.8, 1.2);
    s.rise("[data-part=what]", 2.2, { y: 24, sound: false });
    s.tl.fromTo(".duo-seg", { scaleX: 0 }, { scaleX: 1, duration: 0.6, stagger: 0.12, ease: "power3.out" }, 2.6);
    s.sound("zip", 2.6, 1, { length: 0.7 });
    s.rise("[data-part=ways] > *", 2.9, { y: 20, stagger: 0.1, sound: false });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div className="kick">The chat's duo</div>
          <div data-part="lead" className="lead">
            The two who replied to each other the most
          </div>
        </Zone>
        <Zone at="middle">
          <div data-part="a" className="abs" style={{ left: 150, top: 470 }}>
            <Avatar name={duo.a} size={360} />
          </div>
          <div data-part="b" className="abs" style={{ left: 570, top: 470 }}>
            <Avatar name={duo.b} size={360} />
          </div>
          <div data-part="and" className="abs disp" style={{ left: 0, right: 0, top: 560, textAlign: "center", fontSize: 200, color: "var(--wr-white)" }}>
            &amp;
          </div>
          <div data-part="names" className="abs" style={{ left: 72, right: 72, top: 870, display: "flex", justifyContent: "space-between", gap: 40 }}>
            <Fit size={84} className="disp" style={{ width: 430 }}>
              {duo.a}
            </Fit>
            <Fit size={84} className="disp" style={{ width: 430, textAlign: "right" }}>
              {duo.b}
            </Fit>
          </div>
        </Zone>
        <Zone at="bottom">
          <div data-part="total" className="abs disp" style={{ left: 66, top: 1130, fontSize: 250, fontVariantNumeric: "tabular-nums" }}>
            {count(total)}
          </div>
          <div data-part="what" className="line" style={{ top: 1390 }}>
            replies between them this year.
          </div>
          <div className="abs" style={{ left: 72, top: 1500, width: BAR, height: 64 }}>
            <div className="duo-seg seg" style={{ left: 0, width: split, height: 64, background: "var(--wr-ink)" }} />
            <div className="duo-seg seg" style={{ left: split + 10, width: BAR - 10 - split, height: 64, background: "var(--wr-white)" }} />
          </div>
          <div data-part="ways" className="abs mono" style={{ left: 72, right: 72, top: 1590, display: "flex", justifyContent: "space-between", fontSize: 28, fontWeight: 700 }}>
            <span>
              {duo.a} → {count(duo.a_to_b)}
            </span>
            <span>
              {count(duo.b_to_a)} ← {duo.b}
            </span>
          </div>
        </Zone>
      </Scene>
    </div>
  );
}
